import { Box, CircularProgress, Divider, Icon, Typography } from '@mui/material'
import AdxStatusBar from 'components/reusable/statusBar/AdxStatusBar'
import AdxDropdownWithFilter from 'components/reusable/AdxDropdownWithFilter'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import EmployeeList from 'module/payroll/global-components/EmployeeList'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Form, Formik } from 'formik'
import ProRata from '../components/ProRata'
import OvertimeComponent from '../components/OvertimeComponent'
import CustomAdditional from '../components/CustomAdditional'
import CustomDeduction from '../components/CustomDeduction'
import ClaimsModal from '../components/ClaimsModal'
import DetailSummary from '../components/DetailSummary'
import ManualStatutoryAdjustmentModal from '../components/ManualStatutoryAdjustmentModal'
import * as yup from 'yup'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  getCustomCategory,
  postCalculatePayroll,
  postEditStatutoryContribution,
  postSubmitPayroll,
} from 'services/api/modules/payroll/payroll-edit'
import { retrieveEmployeeData } from 'services/api/modules/payroll/payroll-view'
import Bugsnag from '@bugsnag/js'
import AdxInfoModal from 'components/reusable/modal/AdxInfoModal'
import AdxButton from 'components/custom/AdxButton'
import {
  addEmployeeToPayroll,
  excludeSelectedEmployee,
  removeExcludedEmployee,
  removeEmployeeFromPayroll,
  retrieveExcludedEmployees,
} from 'services/api/modules/payroll/payroll-config/excludeEmployeeApi'
import { ListSubheader, MenuItem } from '@mui/material'
import useNetworkStatus from 'services/hooks/useNetworkStatus'
import { toast } from 'react-toastify'

const PayrollEdit = ({ time_id, details }) => {
  const { isOffline } = useNetworkStatus()
  const normalizeMoneyValue = (value) => `${value ?? ''}`.replace(/,/g, '').trim()
  const getStatutoryFormValues = (employee) => ({
    epf: {
      employee: normalizeMoneyValue(employee?.epf?.employee),
      employer: normalizeMoneyValue(employee?.epf?.employer),
    },
    socso: {
      employee: normalizeMoneyValue(employee?.socso?.employee),
      employer: normalizeMoneyValue(employee?.socso?.employer),
    },
    skbbk: {
      employee: normalizeMoneyValue(employee?.skbbk?.employee),
    },
    eis: {
      employee: normalizeMoneyValue(employee?.eis?.employee),
      employer: normalizeMoneyValue(employee?.eis?.employer),
    },
    mtd: {
      current: normalizeMoneyValue(employee?.mtd?.nett_mtd),
      remarks: employee?.mtd?.mtd_remarks?.trim?.() || '',
    },
  })
  const hasManualStatutoryChanges = (employee, formValues) => {
    const currentValues = getStatutoryFormValues(employee)

    return (
      currentValues.epf.employee !== formValues.epf.employee ||
      currentValues.epf.employer !== formValues.epf.employer ||
      currentValues.socso.employee !== formValues.socso.employee ||
      currentValues.socso.employer !== formValues.socso.employer ||
      currentValues.skbbk.employee !== formValues.skbbk.employee ||
      currentValues.eis.employee !== formValues.eis.employee ||
      currentValues.eis.employer !== formValues.eis.employer ||
      currentValues.mtd.current !== formValues.mtd.current ||
      currentValues.mtd.remarks !== formValues.mtd.remarks
    )
  }
  const applyManualStatutoryPreview = (employee, formValues) => {
    if (!employee) return employee

    return {
      ...employee,
      epf: {
        ...employee.epf,
        employee: formValues.epf.employee,
        employer: formValues.epf.employer,
      },
      socso: {
        ...employee.socso,
        employee: formValues.socso.employee,
        employer: formValues.socso.employer,
      },
      skbbk: {
        ...employee.skbbk,
        employee: formValues.skbbk.employee,
      },
      eis: {
        ...employee.eis,
        employee: formValues.eis.employee,
        employer: formValues.eis.employer,
      },
      mtd: {
        ...employee.mtd,
        nett_mtd: formValues.mtd.current,
        mtd_remarks: formValues.mtd.remarks,
      },
    }
  }
  const getBaseEmployeeDetails = (employeeId, employeeList, fallback) =>
    employeeList?.find((item) => item.id === employeeId) || fallback
  const buildPayrollPayload = (values) => {
    let data
    const { add_item, deduct_item, ...rest } = values
    let additional_items = add_item.map((item) => {
      const { category_items, value, ...itemRest } = item
      const { id: additional_items_id } = category_items
      return {
        category_id: additional_items_id,
        value: value,
        ...itemRest,
      }
    })
    let deduction_item = deduct_item.map((item) => {
      const { category_items, value, ...itemRest } = item
      const { id: deduction_items_id } = category_items
      return {
        category_id: deduction_items_id,
        value: value,
        ...itemRest,
      }
    })

    if (additional_items.length > 0 && deduction_item.length > 0) {
      data = { additional_items, deduction_item, ...rest }
    } else if (additional_items.length > 0 && deduction_item.length === 0) {
      data = { additional_items, ...rest }
    } else if (deduction_item.length > 0 && additional_items.length === 0) {
      data = { deduction_item, ...rest }
    } else if (additional_items.length === 0 && deduction_item.length === 0) {
      data = { ...rest }
    }

    return data
  }

  //get employee details
  const { data: retrieveData, isLoading: loadingData } = useQuery(
    ['retrieveEmployeeData', time_id],
    () => retrieveEmployeeData(time_id)
  )
  const [monthYear, setMonthYear] = useState(retrieveData)
  const [isErrorModalOpen, setErrorModal] = useState(false)
  const [errorModalMessage, setErrorModalMessage] = useState(
    'Our team has been notified regarding this issue. You may refresh the page or try again later.'
  )
  const getApiErrorMessage = (error, fallback) =>
    error?.data?.message ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  const showErrorModal = (
    error,
    fallback = 'Our team has been notified regarding this issue. You may refresh the page or try again later.'
  ) => {
    Bugsnag.notify(error)
    setErrorModalMessage(getApiErrorMessage(error, fallback))
    setErrorModal(true)
  }

  useEffect(() => {
    if (retrieveData) {
      setMonthYear(retrieveData)
    }
  }, [retrieveData])

  //post submit
  const queryClient = useQueryClient()
  const { mutateAsync: submitData } = useMutation(postSubmitPayroll, {
    onSuccess: () => {
      const payrollTimeId = parseInt(retrieveData?.time_id)
      queryClient.invalidateQueries(['retrieveEmployeeData'])
      queryClient.invalidateQueries(['retrieveEmployeeData', payrollTimeId])
      setIsLoadingSubmit(false)
      setStateChanges(false)
      setAdjustDays(false)
      setManualStatutoryValues(null)
      navigate(`/app/payroll/regular-payroll/${monthYear.month}-${parseInt(monthYear.year)}`, {
        state: { item: payrollTimeId },
      })
    },
    onError: (error) => {
      showErrorModal(error)
    },
  })
  //post calculation
  const { mutateAsync: calculateEmployeeData, data: newCalculateData } = useMutation(
    postCalculatePayroll,
    {
      onSuccess: () => {
        submissionButtonRef.current = 'submit'
        setSubmissionButton('submit')
        setStateChanges(false)
        setAdjustDays(false)
      },
      onError: (error) => {
        submissionButtonRef.current = 'submit'
        setSubmissionButton('submit')
        setAdjustDays(false)
        showErrorModal(error)
      },
    }
  )

  // get custom category list
  const { data: customCategory } = useQuery('getCustomCategory', async () => {
    const { data } = await getCustomCategory()
    let overtimeData = data?.data?.categories
    return overtimeData
  })

  // check status true or false for
  const [adjustDays, setAdjustDays] = useState(false)
  const [manualStatutoryValues, setManualStatutoryValues] = useState(null)
  const [isStatutoryModalOpen, setIsStatutoryModalOpen] = useState(false)
  const [isStatutorySaving, setIsStatutorySaving] = useState(false)

  const [selectValue, setSelectValue] = useState(details ? details : '')
  const [selectedEmployeeOption, setSelectedEmployeeOption] = useState(retrieveData?.data)
  const [dropdownEmployeeOptions, setDropdownEmployeeOptions] = useState([])

  const excludedEmployeesData = useMemo(
    () => retrieveData?.excluded_employees || [],
    [retrieveData?.excluded_employees]
  )
  const loadingExcluded = loadingData
  const { data: globalExcludedResponse, isLoading: loadingGlobalExcluded } = useQuery(
    ['globalPayrollExcludedEmployees'],
    retrieveExcludedEmployees
  )

  const globalExcludedEmployees = useMemo(
    () => globalExcludedResponse?.data || globalExcludedResponse || [],
    [globalExcludedResponse]
  )

  const includedEmployees = useMemo(() => {
    if (!retrieveData?.data) return []
    return retrieveData.data
  }, [retrieveData?.data])

  const globalExcludedEmployeesForDropdown = useMemo(() => {
    const currentPayrollEmployeeIds = new Set([
      ...includedEmployees.map((employee) => employee.id),
      ...excludedEmployeesData.map((employee) => employee.employee_id),
    ])

    return globalExcludedEmployees.filter(
      (employee) => !currentPayrollEmployeeIds.has(employee.employee_id)
    )
  }, [excludedEmployeesData, globalExcludedEmployees, includedEmployees])

  const employeeDropdownSearchOptions = useMemo(
    () => [
      ...includedEmployees.map((employee) => ({
        ...employee,
        employee,
        payroll_dropdown_group: 'included',
      })),
      ...excludedEmployeesData.map((employee) => ({
        ...employee,
        id: employee.employee_id,
        employee,
        payroll_dropdown_group: 'run_excluded',
      })),
      ...globalExcludedEmployeesForDropdown.map((employee) => ({
        ...employee,
        id: employee.employee_id,
        employee,
        payroll_dropdown_group: 'global_excluded',
      })),
    ],
    [excludedEmployeesData, globalExcludedEmployeesForDropdown, includedEmployees]
  )

  const filteredIncludedEmployees = dropdownEmployeeOptions
    .filter((employee) => employee.payroll_dropdown_group === 'included')
    .map((employee) => employee.employee)

  const filteredRunExcludedEmployees = dropdownEmployeeOptions
    .filter((employee) => employee.payroll_dropdown_group === 'run_excluded')
    .map((employee) => employee.employee)

  const filteredGlobalExcludedEmployees = dropdownEmployeeOptions
    .filter((employee) => employee.payroll_dropdown_group === 'global_excluded')
    .map((employee) => employee.employee)

  useEffect(() => {
    setDropdownEmployeeOptions(employeeDropdownSearchOptions)
  }, [employeeDropdownSearchOptions])


  // useEffect(() => {
  //   if (newCalculateData) {
  //     const recalculatedEmployee = newCalculateData?.data?.data?.data
  //     setSelectValue(recalculatedEmployee)
  //     setSelectedEmployeeOption((prev = []) =>
  //       prev.map((item) => (item.id === recalculatedEmployee?.id ? recalculatedEmployee : item))
  //     )
  //   } else {
  //     setSelectedEmployeeOption(retrieveData?.data)
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [newCalculateData, retrieveData])
  useEffect(() => {
    if (newCalculateData) {
      const recalculatedEmployee = newCalculateData?.data?.data?.data
      setSelectValue(recalculatedEmployee)
      setSelectedEmployeeOption((prev = []) =>
        prev.map((item) => (item.id === recalculatedEmployee?.id ? recalculatedEmployee : item))
      )
    } else {
      setSelectedEmployeeOption(includedEmployees)
    }
  }, [newCalculateData, retrieveData, includedEmployees])

  useEffect(() => {
    setManualStatutoryValues(null)
  }, [selectValue?.id])

  // detect any changes for calculation button to appear
  const [stateChanges, setStateChanges] = useState(false)

  //custom additional categories
  const [addCustomItem, setAddCustomItem] = useState([])
  useEffect(() => {
    if (customCategory) {
      let arr = customCategory
        ?.sort((a, b) => (a.status > b.status ? 1 : -1))
        ?.filter((item) => {
          if (
            item.custom_status === 0 ||
            item.custom_status === 1 ||
            item.custom_status === 2 ||
            item.custom_status === 3
          ) {
            return item
          } else {
            return null
          }
        })
      setAddCustomItem(arr)
    }
  }, [customCategory])

  //custom deduction categories
  const [deductCustomItem, setDeductCustomItem] = useState([])
  useEffect(() => {
    if (customCategory) {
      let arr = customCategory
        ?.sort((a, b) => (a.status > b.status ? 1 : -1))
        ?.filter((item) => {
          if (item.custom_status === 4 || item.custom_status === 5) {
            return item
          } else {
            return null
          }
        })
      setDeductCustomItem(arr)
    }
  }, [customCategory])

  //state of employee selected
  const [additionalItem, setAdditionalItem] = useState()
  const [deductionItem, setDeductionItem] = useState()
  useEffect(() => {
    let arrAdditional = []
    setAdditionalItem(arrAdditional)
    let arrDeduction = []
    setDeductionItem(arrDeduction)
    if (selectValue) {
      selectValue?.additional?.map((item) => {
        if (item.status === 0 || item.status === 1 || item.status === 2 || item.status === 3) {
          return arrAdditional.push(item)
        } else if (item.status === 4 || item.status === 5) {
          return arrDeduction.push(item)
        }
        return undefined
      })
    }
  }, [selectValue])

  const navigate = useNavigate()
  const onClose = () => {
    navigate(`/app/payroll/regular-payroll/${monthYear.month}-${parseInt(monthYear.year)}`, {
      state: { item: time_id },
    })
  }
  // approved claims of the selected employee (only when Claims is connected to payroll)
  const claimSummary = retrieveData?.claims_in_payroll
    ? retrieveData?.claim_summary?.[selectValue?.id]
    : null

  const refButton = useRef()
  const submissionButtonRef = useRef('submit')
  const submit = (button = 'submit') => {
    if (isOffline) {
      toast.error('Connection lost. Please reconnect and try again.')
      return
    }

    submissionButtonRef.current = button
    setSubmissionButton(button)
    refButton.current.handleSubmit()
  }
  const handleSetSelectValue = (e) => {
    setSelectValue(e.target.value)
    setManualStatutoryValues(null)
    setStateChanges(false)
    setAdjustDays(false)
  }

  useEffect(() => {
    if (selectValue) {
      setSelectValue(selectedEmployeeOption?.find((el) => el.id === selectValue.id))
    } else {
      setSelectValue('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployeeOption])

  //formik
  const [, setSubmissionButton] = useState('submit')
  //validation schema
  const validationSchema = yup.object().shape({
    quantity:
      selectValue?.salary?.salary_category !== 'Hourly'
        ? yup
          .number()
          .min(0.01, 'This field must be a valid number')
          .max(31, 'The value cannot exceed 31 days')
          .nullable()
          .transform((value) => (isNaN(value) ? undefined : value))
          .required('This field is required')
        : yup
          .number()
          .min(0.01, 'This field must be a valid number')
          .max(744, 'The value cannot exceed 744 hours')
          .nullable()
          .transform((value) => (isNaN(value) ? undefined : value))
          .required('This field is required'),
    add_item: yup.array().of(
      yup
        .object()
        .nullable()
        .shape({
          value: yup
            .number()
            .typeError('This field must contain only number')
            .min(0.01, 'This field must be more than RM 0.00')
            .nullable()
            .required('This field is required'),
          // .matches(
          //   /(^\d{1,3}(,\d{3})*(\.[0-9]{1,2})*$)|(^[0-9]*(\.[0-9]{1,2})*$)/,
          //   'This field only accept a valid number format'
          // ),
          category_items: yup.object().required('This field is required'),
        })
    ),
    deduct_item: yup.array().of(
      yup
        .object()
        .nullable()
        .shape({
          value: yup
            .number()
            .typeError('This field must contain only number')
            .min(0.01, 'This field must be more than RM 0.00')
            .nullable()
            .required('This field is required'),
          // .matches(
          //   /(^\d{1,3}(,\d{3})*(\.[0-9]{1,2})*$)|(^[0-9]*(\.[0-9]{1,2})*$)/,
          //   'This field only accept a valid number format'
          // ),
          category_items: yup.object().required('This field is required'),
        })
    ),
  })
  const handleSubmission = async (values) => {
    const employeeTimeId = parseInt(monthYear?.time_id)
    const employeeId = selectValue?.id

    try {
      let data = buildPayrollPayload(values)

      if (manualStatutoryValues) {
        setIsStatutorySaving(true)
        await postEditStatutoryContribution({
          time_id: employeeTimeId,
          employee_id: employeeId,
          data: manualStatutoryValues,
        })
        data = {
          ...data,
          preserve_manual_statutory: true,
        }
      }

      if (submissionButtonRef.current === 'submit') {
        await handleSubmitConfirmation({
          time_id: employeeTimeId,
          employee_id: employeeId,
          data,
        })
      } else {
        await handleCalculate({
          time_id: employeeTimeId,
          employee_id: employeeId,
          data,
        })
      }
    } catch (error) {
      showErrorModal(error)
      setIsLoadingSubmit(false)
    } finally {
      setIsStatutorySaving(false)
    }
  }

  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false)

  const handleSubmitConfirmation = async ({ time_id, employee_id, data }) => {
    setIsLoadingSubmit(true)
    await submitData({ time_id, employee_id, data })
  }
  const handleCalculate = async ({ time_id, employee_id, data }) => {
    await calculateEmployeeData({ time_id, employee_id, data })
  }
  const handleApplyManualStatutory = (values) => {
    const baseEmployee = getBaseEmployeeDetails(selectValue?.id, selectedEmployeeOption, details)
    const hasChanges = hasManualStatutoryChanges(baseEmployee, values)
    setManualStatutoryValues(hasChanges ? values : null)
    if (hasChanges) {
      setSelectValue((prev) => applyManualStatutoryPreview(prev, values))
      setStateChanges(true)
    } else {
      setSelectValue(baseEmployee)
    }
    setIsStatutoryModalOpen(false)
  }

  const { mutate: excludeEmployee } = useMutation(
    async (employee_id) => {
      await removeEmployeeFromPayroll(time_id, employee_id)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['retrieveEmployeeData', time_id])
      },
      onError: (error) => {
        showErrorModal(error, 'Unable to remove this employee from payroll. Please try again.')
      },
    }
  )

  const { mutate: includeEmployee } = useMutation(
    async (employee_id) => {
      await addEmployeeToPayroll(time_id, employee_id)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['retrieveEmployeeData', time_id])
      },
      onError: (error) => {
        showErrorModal(error, 'Unable to add this employee into payroll. Please try again.')
      },
    }
  )

  const { mutate: includeGlobalExcludedEmployee } = useMutation(
    async (employee_id) => {
      await removeExcludedEmployee(employee_id)

      try {
        await addEmployeeToPayroll(time_id, employee_id)
      } catch (error) {
        await excludeSelectedEmployee({ employee_ids: [employee_id] }).catch(() => {})
        throw error
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['retrieveEmployeeData', time_id])
        queryClient.invalidateQueries(['globalPayrollExcludedEmployees'])
      },
      onError: (error) => {
        showErrorModal(error, 'Unable to add this employee into payroll. Please try again.')
      },
    }
  )

  const handleExcludeEmployee = (employee) => {
    const empId = employee.id ?? employee.employee_id
    if (selectValue?.id === empId) {
      setSelectValue('')
      setStateChanges(false)
      setManualStatutoryValues(null)
      setAdjustDays(false)
    }
    excludeEmployee(empId)
  }

  return (
    <Formik
      innerRef={refButton}
      enableReinitialize
      validationSchema={validationSchema}
      initialValues={{
        quantity: selectValue?.salary?.quantity,
        employee_status: selectValue?.status,
        add_item:
          additionalItem?.length === 0
            ? []
            : selectValue?.additional
              ?.filter((item) => {
                if (
                  item.status === 0 ||
                  item.status === 1 ||
                  item.status === 2 ||
                  item.status === 3
                ) {
                  return item
                } else {
                  return null
                }
              })
              .map((list) => ({
                category_items: {
                  id: list.id,
                  item: list.title,
                  status: list.status,
                },
                repeat: 0,
                value:
                  typeof list.value === 'string'
                    ? parseFloat(list.value?.replace(/,/g, '')).toFixed(2)
                    : list.value,
              })),
        deduct_item:
          deductionItem?.length === 0
            ? []
            : selectValue?.additional
              ?.filter((item) => {
                if (item.status === 4 || item.status === 5) {
                  return item
                } else {
                  return null
                }
              })
              ?.map((list) => ({
                category_items: {
                  id: list.id,
                  item: list.title,
                  status: list.status,
                },
                repeat: 0,
                value:
                  typeof list.value === 'string'
                    ? parseFloat(list.value?.replace(new RegExp(',', 'g'), '')).toFixed(2)
                    : list.value,
              })),
      }}
      onSubmit={(values) => handleSubmission(values)}
    >
      {(formikProps) => {
        return (
          <Form>
            <Box sx={{ display: 'flex', width: '100vw' }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: 'calc(100vw * 2 / 3)',
                  minHeight: '100vh',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 4,
                    py: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="h4" fontWeight={600} sx={{ mr: 2 }}>
                      Adjust payroll
                    </Typography>
                    {!loadingData && (
                      <AdxStatusBar
                        status={{
                          text: `${monthYear?.month} ${monthYear?.year}`,
                          bgColor: 'alternate.light',
                          color: 'alternate.main',
                        }}
                      />
                    )}
                  </Box>
                  <Icon onClick={onClose} sx={{ cursor: 'pointer' }}>
                    close
                  </Icon>
                </Box>

                <Divider />
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    height: 'calc( 100vh - 60px)',
                    overflow: 'auto',
                  }}
                  className="scrollbar"
                >
                  <Box sx={{ px: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* <AdxDropdownWithFilter
                      allOptions={retrieveData?.data}
                      setOptions={setSelectedEmployeeOption}
                      filters={['name']}
                      value={selectValue}
                      onChange={handleSetSelectValue}
                      sx={{
                        '& .MuiOutlinedInput-root': { height: '55px' },
                        marginY: 2,
                      }}
                      renderValue={(option) => {
                        return (
                          <EmployeeList
                            sxAvatar={{
                              minWidth: '30px',
                              maxWidth: '30px',
                              height: '30px',
                            }}
                            avatar={option ? option.avatar : details.avatar}
                            name={option ? option.name : details.name}
                            position={option ? option.position : details.position}
                          />
                        )
                      }}
                    >
                      {selectedEmployeeOption?.map((item, index) => {
                        return (
                          <AdxMenuItem selectedvalue={selectValue} key={index} value={item}>
                            <EmployeeList
                              avatar={item.avatar}
                              sxAvatar={{ width: '30px', height: '30px' }}
                              name={item.name}
                              position={item.position}
                            />
                          </AdxMenuItem>
                        )
                      })}
                    </AdxDropdownWithFilter> */}
                    <AdxDropdownWithFilter
                      allOptions={employeeDropdownSearchOptions}
                      setOptions={setDropdownEmployeeOptions}
                      filters={['name', 'position', 'department']}
                      value={selectValue}
                      onChange={handleSetSelectValue}
                      sx={{
                        '& .MuiOutlinedInput-root': { height: '55px' },
                        marginY: 2,
                      }}
                      renderValue={(option) => (
                        <EmployeeList
                          sxAvatar={{ minWidth: '30px', maxWidth: '30px', height: '30px' }}
                          avatar={option ? option.avatar : details.avatar}
                          name={option ? option.name : details.name}
                          position={option ? option.position : details.position}
                        />
                      )}
                    >
                      {/* ── INCLUDED EMPLOYEES ── */}
                      <ListSubheader sx={{ lineHeight: '0', py: 1, position: 'static' }}>
                        <Typography variant="caption" color="light-gray-text.main">
                          INCLUDED EMPLOYEES
                        </Typography>
                      </ListSubheader>

                      {filteredIncludedEmployees?.length === 0 && (
                        <MenuItem disabled>
                          <Typography variant="body2" color="light-gray-text.main">No employees</Typography>
                        </MenuItem>
                      )}

                      {filteredIncludedEmployees?.map((item, index) => (
                        <AdxMenuItem selectedvalue={selectValue} key={index} value={item}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                            <EmployeeList
                              avatar={item.avatar}
                              sxAvatar={{ width: '30px', height: '30px' }}
                              name={item.name}
                              position={item.position}
                            />
                            <Typography
                              component="span"
                              variant="caption"
                              color="error.main"
                              sx={{ cursor: 'pointer', ml: 2, whiteSpace: 'nowrap', textDecoration: 'underline' }}
                              onClick={(e) => {
                                e.stopPropagation()  // prevent dropdown onChange from firing
                                handleExcludeEmployee(item)
                              }}
                            >
                              Remove
                            </Typography>
                          </Box>
                        </AdxMenuItem>
                      ))}

                      {/* ── EXCLUDED EMPLOYEES ── */}
                      <ListSubheader sx={{ lineHeight: '0', py: 1, position: 'static' }}>
                        <Typography variant="caption" color="light-gray-text.main">
                          EXCLUDED EMPLOYEES
                        </Typography>
                      </ListSubheader>

                      {loadingExcluded && (
                        <MenuItem disabled>
                          <Typography variant="body2" color="light-gray-text.main">Loading...</Typography>
                        </MenuItem>
                      )}

                      {!loadingExcluded && filteredRunExcludedEmployees?.length === 0 && (
                        <MenuItem disabled>
                          <Typography variant="body2" color="light-gray-text.main">No excluded employees</Typography>
                        </MenuItem>
                      )}

                      {filteredRunExcludedEmployees?.map((item, index) => (
                        <MenuItem
                          key={`excluded-${index}`}
                          disableRipple
                          // no `value` prop so MUI Select won't treat this as a selectable option
                          sx={{ pointerEvents: 'none', opacity: 0.7 }}
                        >
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            width="100%"
                            sx={{ pointerEvents: 'auto' }} // re-enable pointer events inside the row
                          >
                            <EmployeeList
                              avatar={item.avatar}
                              sxAvatar={{ width: '30px', height: '30px' }}
                              name={item.name}
                              position={item.position}
                            />
                            <Typography
                              component="span"
                              variant="caption"
                              color="primary.main"
                              sx={{ cursor: 'pointer', ml: 2, whiteSpace: 'nowrap', textDecoration: 'underline' }}
                              onClick={(e) => {
                                e.stopPropagation()
                                includeEmployee(item.employee_id)  // note: excluded API uses employee_id, not id
                              }}
                            >
                              + Add
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}

                      {loadingGlobalExcluded && (
                        <MenuItem disabled>
                          <Typography variant="body2" color="light-gray-text.main">Loading...</Typography>
                        </MenuItem>
                      )}

                      {!loadingGlobalExcluded && filteredGlobalExcludedEmployees?.length === 0 && (
                        <MenuItem disabled>
                          <Typography variant="body2" color="light-gray-text.main">No globally excluded employees</Typography>
                        </MenuItem>
                      )}

                      {filteredGlobalExcludedEmployees?.map((item, index) => (
                        <MenuItem
                          key={`global-excluded-${index}`}
                          disableRipple
                          sx={{ pointerEvents: 'none', opacity: 0.7 }}
                        >
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            width="100%"
                            sx={{ pointerEvents: 'auto' }}
                          >
                            <EmployeeList
                              avatar={item.avatar}
                              sxAvatar={{ width: '30px', height: '30px' }}
                              name={item.name}
                              position={item.position}
                            />
                            <Typography
                              component="span"
                              variant="caption"
                              color="primary.main"
                              sx={{ cursor: 'pointer', ml: 2, whiteSpace: 'nowrap', textDecoration: 'underline' }}
                              onClick={(e) => {
                                e.stopPropagation()
                                includeGlobalExcludedEmployee(item.employee_id)
                              }}
                            >
                              + Add
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </AdxDropdownWithFilter>
                    {/* work days */}
                    {selectValue && (
                      <ProRata
                        adjustDays={adjustDays}
                        setAdjustDays={setAdjustDays}
                        formikProps={formikProps}
                        selectValue={selectValue}
                        setStateChanges={setStateChanges}
                      />
                    )}
                    {/* overtime and claims */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: 1 }}>
                      <OvertimeComponent selectValue={selectValue} data={monthYear} />
                      {selectValue && claimSummary && (
                        <ClaimsModal
                          time_id={parseInt(monthYear?.time_id)}
                          period={`${monthYear?.month} ${monthYear?.year}`}
                          employee={selectValue}
                        >
                          <AdxButton variant={'ghost'} sx={{ textDecoration: 'underline' }}>
                            {claimSummary.included_count > 0
                              ? `Edit claims (RM ${Number(claimSummary.included_amount).toFixed(2)} included)`
                              : `Add claims (${claimSummary.available})`}
                          </AdxButton>
                        </ClaimsModal>
                      )}
                    </Box>
                    {selectValue && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* credit */}
                        <CustomAdditional
                          addCustomItem={addCustomItem}
                          formikProps={formikProps}
                          setStateChanges={setStateChanges}
                        />
                        {/* deduction */}
                        <CustomDeduction
                          deductCustomItem={deductCustomItem}
                          formikProps={formikProps}
                          setStateChanges={setStateChanges}
                        />
                      </Box>
                    )}
                  </Box>
                  {/* button */}
                  <Box
                    sx={{
                      px: 4,
                      py: 2,
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      gap: 2,
                      position: 'sticky',
                      bottom: 0,
                      backgroundColor: 'white.main',
                      borderTop: 1,
                      borderColor: 'gray-line.main',
                    }}
                  >
                    <AdxButton variant={'ghost'} onClick={onClose}>
                      Cancel
                    </AdxButton>
                    <AdxButton
                      variant={'contained'}
                      disabled={selectValue === '' || selectValue === undefined || isOffline}
                      sx={{ width: 'calc(100% * 1 / 2)' }}
                      onClick={() => submit('submit')}
                    >
                      {isLoadingSubmit ? (
                        <>
                          <CircularProgress color="white" size={20} />
                        </>
                      ) : (
                        <>Submit</>
                      )}
                    </AdxButton>
                  </Box>
                </Box>
              </Box>
              <DetailSummary
                stateChanges={stateChanges}
                time_id={time_id}
                data={selectValue}
                onCalculate={() => submit('calculationButton')}
                onEditStatutory={() => setIsStatutoryModalOpen(true)}
                isActionDisabled={isOffline}
              />
            </Box>
            <AdxInfoModal
              open={isErrorModalOpen}
              onClose={() => setErrorModal(false)}
              handleClose={() => setErrorModal(false)}
              muiIcon="error"
              iconColor="error.main"
              title="An error occured"
              subtitle={errorModalMessage}
              footerButtons
              firstBtnLabel={'Close'}
              firstBtnHandle={() => setErrorModal(false)}
            ></AdxInfoModal>
            <ManualStatutoryAdjustmentModal
              open={isStatutoryModalOpen}
              handleClose={() => setIsStatutoryModalOpen(false)}
              data={selectValue}
              values={manualStatutoryValues}
              onApply={handleApplyManualStatutory}
              isLoading={isStatutorySaving}
            />
          </Form>
        )
      }}
    </Formik>
  )
}

export default PayrollEdit
